import { InputField, useKeyPress } from "ui-components";
import "./FollowUpChat.scss";
import VerticalBar from "./VerticalBar";
import { useTranslation } from "react-i18next";
import SendIcon from "../../utils/Images/SendIcon";
import { useEffect, useRef, useState } from "react";
import {
  getFollowupMessage,
  postFollowupMessage,
} from "../../service/CreatedAssessmentListAndReview/CreatedAssessmentListAndReview";
import { convertDate, getTimeDifference } from "../../utils/Common/Constant";
import DownArrow from "../../utils/Images/DownArrow";
import Loader from "../Loader/Loader";

interface FollowUpChatProps {
  assessmentTitle: string;
  applicantId: string;
  assessmentId: string;
  invitedOn: any;
  submittedOn: any;
  resultStatus: any;
}
const FollowUpChat = ({
  assessmentTitle,
  applicantId,
  assessmentId,
  invitedOn,
  submittedOn,
  resultStatus,
}: FollowUpChatProps) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<any>([]);
  const [messageText, setMessageText] = useState("");
  const [lastMessageTime, setLastMessageTime] = useState("");
  const [loading, setLoading] = useState(false); // Loader state
  const messageSectionRef = useRef<HTMLDivElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const isUserScrollingRef = useRef<boolean>(false);
  const [sendBtnLoading, setSendBtnLoading] = useState(false);
  const [totalMsgCount, setTotalMsgCount] = useState(0);
  useKeyPress(
    "Enter",
    () => postFollowupMessageApi(),
    messageText?.trim()?.length > 0,
  );

  useEffect(() => {
    getFollowupMessageApi("");
  }, []);

  const handleScrollToTop = () => {
    if (messageSectionRef.current?.scrollTop === 0) {
      isUserScrollingRef.current = true;
      const prevScrollHeight = messageSectionRef.current.scrollHeight;
      if (messages.length < totalMsgCount) {
        getFollowupMessageApi(lastMessageTime).then(() => {
          requestAnimationFrame(() => {
            if (messageSectionRef.current) {
              const newScrollHeight = messageSectionRef.current.scrollHeight;
              const scrollOffset = newScrollHeight - prevScrollHeight;
              messageSectionRef.current.scrollTop += scrollOffset + 20; // Maintain current position
            }
          });
        });
      }
    }
  };
  // get messages api
  const getFollowupMessageApi = async (messageTime: string) => {
    setLoading(true);
    try {
      const res = await getFollowupMessage(
        applicantId,
        assessmentId,
        messageTime,
      );
      if (res?.status === 200) {
        const messagesArray = [...res.data.data].reverse();
        setMessages((prev: any) => {
          const uniqueMessages = messagesArray.filter(
            (msg) =>
              !prev.some((prevMsg: any) => prevMsg.messageId === msg.messageId),
          );
          return [...uniqueMessages, ...prev];
        });
        setTotalMsgCount(res?.data?.totalCount);
        if (messagesArray.length > 0) {
          setLastMessageTime(messagesArray[0]?.createdAt);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  // post followup message
  const postFollowupMessageApi = async () => {
    const data = {
      assessmentId,
      userType: "Company",
      companyId: localStorage.getItem("companyId") ?? "",
      applicantId,
      messageType: "Text",
      messageText,
    };

    setSendBtnLoading(true); // Show loader before sending message
    isUserScrollingRef.current = false;
    try {
      const res = await postFollowupMessage(data);
      if (res?.status === 200) {
        setLastMessageTime("");
        const newMessages = await getFollowupMessage(
          applicantId,
          assessmentId,
          "",
        );
        if (newMessages?.status === 200) {
          setMessages([...newMessages.data.data].reverse());
          setMessageText("");
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setSendBtnLoading(false); // Hide loader after message sent
    }
  };

  useEffect(() => {
    if (!isUserScrollingRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "instant" });
    }
  }, [messages]);

  const maxMessageLength = 512;
  const timelineData = [
    {
      title: t("GENERAL.INVITED_ON"),
      description: invitedOn ? convertDate(invitedOn, false, true) : "-",
    },
    {
      title: t("GENERAL.SUBMITTED_ON"),
      description: submittedOn ? convertDate(submittedOn, false, true) : "-",
    },
    {
      title: t("GENERAL.CURRENT_STATUS"),
      description:
        resultStatus === "Hold"
          ? t("RESPONSE_LIST.ON_HOLD")
          : (resultStatus ?? "-"),
    },
  ];
  const collapsedTimelineData = [
    {
      title: t("GENERAL.CURRENT_STATUS"),
      description:
        resultStatus === "Hold"
          ? t("RESPONSE_LIST.ON_HOLD")
          : (resultStatus ?? "-"),
    },
  ];
  const [collaps, setCollapse] = useState(true);

  return (
    <div className={`${!collaps && "openStatusTimeline"} followUpChat`}>
      {/* Assessment Name */}
      <div className="assessmentName">
        <span>{assessmentTitle}</span>
        <div className="seperatorHalf">
          <hr />
        </div>
      </div>
      {/* Status Timeline */}
      <div
        className="verticalBarContainer cursorPointer"
        onClick={() => setCollapse(!collaps)}
      >
        <VerticalBar
          items={collaps ? collapsedTimelineData : timelineData}
          dotSize={6}
          lineHeight={60}
          spacing={3}
          outlineSize={15}
        />
        <div className="statusTimeline">
          <span className={`${!collaps ? "downArrow" : "reverseArrow"} `}>
            <DownArrow />
          </span>
        </div>
      </div>
      <div className="seperator">
        <hr />
      </div>

      {/* Messages List with Loader */}
      <div
        className="messageSection"
        ref={messageSectionRef}
        onScroll={handleScrollToTop}
      >
        {loading && (
          <div className="scrollUpLoaderSec">
            <div className="scrollUpLoader">
              <Loader />
            </div>
          </div>
        )}
        <div className="messagesList">
          {messages.map((msg: any, index: any) => (
            <div
              key={index}
              className={`messageBubble ${msg?.reciever === "Applicant" ? "user" : "other"}`}
            >
              <span className="message">{msg?.messageText}</span>
              <span className="time">
                {getTimeDifference(
                  msg?.createdAt,
                  t("GENERAL.MIN(S)"),
                  t("GENERAL.HOUR(S)"),
                  t("GENERAL.DAY(S)"),
                  t("GENERAL.MONTH(S)"),
                  t("GENERAL.YEAR(S)"),
                  t("GENERAL.AGO"),
                  t("GENERAL.JUST_NOW"),
                )}
              </span>
            </div>
          ))}
        </div>
        <div ref={messagesEndRef} />
      </div>
      {/* Message Input */}
      <div className="messageInputContainer">
        <div className="messageInput">
          <InputField
            onChange={(e: any) => setMessageText(e)}
            value={messageText}
            placeholder={t("GENERAL.MESSAGE")}
          />
          <div
            onClick={
              messageText.trim() && messageText.length <= maxMessageLength
                ? postFollowupMessageApi
                : () => {}
            }
            className={
              messageText.trim() && messageText?.length <= maxMessageLength
                ? "cursorPointer"
                : ""
            }
          >
            {sendBtnLoading ? (
              <div className="sendBtnLoader">
                <Loader />
              </div>
            ) : (
              <SendIcon
                color={
                  messageText.trim() && messageText?.length <= maxMessageLength
                    ? "var(--primary-green)"
                    : "var(--light-grey)"
                }
              />
            )}
          </div>
        </div>
        {messageText && (
          <div className="messageCount">
            <span
              className={`${messageText?.length > maxMessageLength && "errorMessage"}`}
            >
              {maxMessageLength - messageText.length}{" "}
              {t("CREATE_ASSESSMENT.TEXT_EDITOR_LIMIT")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowUpChat;
